import React, { useState, useEffect, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './App.css';
import AddTaskModal from './AddTaskModal.js';
import AddEtapaModal from './AddEtapaModal.js';
import CommentsModal from './CommentsModal.js';
import DeadlineReminder from './components/DeadlineReminder.js';
import { useDeadlineReminder } from './hooks/useDeadlineReminder.js';
import { db, auth } from './firebase.js';
import {
    collection, onSnapshot, doc, addDoc, updateDoc,
    getDoc, getDocs, deleteDoc, query, where, orderBy, writeBatch
} from './firestore-compat';

// ============================================================
// MEU PROJETO — etapas e tarefas pessoais por usuário
// Coleção: etapas_projeto_pessoal  (campo userId por documento)
// ============================================================

const ETAPAS_COLL = 'etapas_projeto_pessoal';

const calcularStatusPrazo = (dataFim, status, dataConclusao) => {
    if (status === 'Feito') {
        if (dataConclusao && dataFim) {
            const dataConclusaoObj = new Date(dataConclusao + 'T00:00:00');
            const dataFimObj = new Date(dataFim + 'T00:00:00');
            if (dataConclusaoObj > dataFimObj) {
                const diffDays = Math.ceil((dataConclusaoObj - dataFimObj) / (1000 * 60 * 60 * 24));
                return { texto: `Concluído (${diffDays}d após SLA)`, classe: 'prazo-atrasado' };
            }
            return { texto: 'Concluído no prazo', classe: 'prazo-concluido-ok' };
        }
        return { texto: 'Concluído', classe: 'prazo-concluido-ok' };
    }
    if (!dataFim) return { texto: '--', classe: 'prazo-ok' };
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataFimObj = new Date(dataFim + 'T00:00:00');
    const diffDays = Math.ceil((dataFimObj - hoje) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { texto: `Atrasado (${Math.abs(diffDays)}d)`, classe: 'prazo-atrasado' };
    if (diffDays === 0) return { texto: 'Termina hoje', classe: 'prazo-urgente' };
    return { texto: `Restam ${diffDays}d`, classe: 'prazo-ok' };
};

const calcularDatasDaEtapa = (tarefas) => {
    if (!tarefas || tarefas.length === 0) return { inicio: null, fim: null };
    const com = tarefas.filter(t => t && t.dataInicio && t.dataFim);
    if (com.length === 0) return { inicio: null, fim: null };
    const inics = com.map(t => new Date(t.dataInicio + 'T00:00:00'));
    const fins = com.map(t => new Date(t.dataFim + 'T00:00:00'));
    return {
        inicio: new Date(Math.min(...inics)).toLocaleDateString(),
        fim: new Date(Math.max(...fins)).toLocaleDateString(),
    };
};

const isEtapaConcluida = (tarefas) =>
    tarefas && tarefas.length > 0 && tarefas.every(t => t.status === 'Feito');

// ---- SortableEtapa ----
function SortableEtapa({ etapa, expandedEtapaIds, toggleEtapa, children, ...props }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: etapa.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const concluida = isEtapaConcluida(etapa.tarefas);
    const datas = calcularDatasDaEtapa(etapa.tarefas);

    return (
        <div ref={setNodeRef} style={style}>
            <div className={`etapa-card ${concluida ? 'etapa-concluida' : ''}`}>
                <div className="etapa-info">
                    <span className="drag-handle" {...attributes} {...listeners}>&#x2630;</span>
                    <span className={`arrow ${expandedEtapaIds.includes(etapa.id) ? 'expanded' : ''}`}
                        onClick={() => toggleEtapa(etapa.id)}>&#9654;</span>
                    <input
                        type="text"
                        className="etapa-title-input"
                        style={{ fontWeight: 'bold' }}
                        value={etapa.text}
                        onChange={(e) => props.handleUpdateEtapa(etapa.id, e.target.value)}
                    />
                </div>
                <div className="etapa-datas">
                    {concluida
                        ? <span className="status-concluido">Etapa Concluída</span>
                        : datas.inicio && <><span>Início: {datas.inicio}</span><span>Fim: {datas.fim}</span></>
                    }
                </div>
                <button onClick={() => props.handleDeleteEtapa(etapa.id)} className="delete-etapa-btn">Apagar</button>
            </div>
            {expandedEtapaIds.includes(etapa.id) && (
                <div className="tarefas-container">{children}</div>
            )}
        </div>
    );
}

// ---- PseudoEtapaExterna (Apenas Leitura/Parcial) ----
function PseudoEtapaExterna({ etapa, expandedEtapaIds, toggleEtapa, children }) {
    const concluida = isEtapaConcluida(etapa.tarefas);
    const datas = calcularDatasDaEtapa(etapa.tarefas);

    return (
        <div style={{ marginBottom: '15px' }}>
            <div className={`etapa-card ${concluida ? 'etapa-concluida' : ''}`} style={{ borderLeft: '4px solid #6f42c1', backgroundColor: '#fdfbfe' }}>
                <div className="etapa-info">
                    <span className={`arrow ${expandedEtapaIds.includes(etapa.id) ? 'expanded' : ''}`}
                        onClick={() => toggleEtapa(etapa.id)} style={{ paddingLeft: '8px' }}>&#9654;</span>
                    <strong style={{ fontSize: '1.1rem', color: '#444' }}>{etapa.text}</strong>
                    <span style={{ fontSize: '0.75rem', marginLeft: '10px', background: '#6f42c1', color: '#fff', padding: '2px 8px', borderRadius: '12px' }}>Origem Externa</span>
                </div>
                <div className="etapa-datas">
                    {concluida
                        ? <span className="status-concluido">Etapa Concluída</span>
                        : datas.inicio && <><span>Início: {datas.inicio}</span><span>Fim: {datas.fim}</span></>
                    }
                </div>
            </div>
            {expandedEtapaIds.includes(etapa.id) && (
                <div className="tarefas-container">{children}</div>
            )}
        </div>
    );
}

// ---- Componente principal ----
const MEU_PROJETO_DESATIVADO = true;

function MeuProjeto() {
    const [etapas, setEtapas] = useState([]);
    const [expandedEtapaIds, setExpandedEtapaIds] = useState([]);
    const [userName, setUserName] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Etapas Externas
    const [externalIW, setExternalIW] = useState([]);
    const [externalDir, setExternalDir] = useState([]);
    const [externalHC, setExternalHC] = useState([]);
    const [externalQual, setExternalQual] = useState([]);

    // Modals
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isEtapaModalOpen, setIsEtapaModalOpen] = useState(false);
    const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    // Filtros
    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroPrazo, setFiltroPrazo] = useState('todos');
    const [filtroTexto, setFiltroTexto] = useState('');

    const currentUser = auth.currentUser;

    const allTasksForReminder = useMemo(() => {
        const extTasks = [...externalIW, ...externalDir, ...externalHC, ...externalQual].flatMap(e => e.tarefas || []);
        const myTasks = etapas.flatMap(e => e.tarefas || []);
        return [...myTasks, ...extTasks];
    }, [etapas, externalIW, externalDir, externalHC, externalQual]);

    const { isTodayOpen, isWeekOpen, todayItems, weekItems,
        closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually }
        = useDeadlineReminder(allTasksForReminder, 'meu_projeto', 'task');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // ---- Carregar dados do usuário e etapas ----
    useEffect(() => {
        if (MEU_PROJETO_DESATIVADO || !currentUser) { setIsLoading(false); return; }

        // Buscar nome do usuário
        const loadUser = async () => {
            const snap = await getDocs(query(collection(db, 'users'), where('email', '==', currentUser.email)));
            if (!snap.empty) setUserName(snap.docs[0].data().name || currentUser.displayName || 'Eu');
            else setUserName(currentUser.displayName || 'Eu');
            setIsLoading(false);
        };
        loadUser();

        // Escutar apenas as etapas deste usuário
        const q = query(
            collection(db, ETAPAS_COLL),
            where('userId', '==', currentUser.uid),
            orderBy('order')
        );
        const unsub = onSnapshot(q, (snap) => {
            setEtapas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => console.error('Erro ao carregar Meu Projeto:', err));

        return () => { unsub(); };
    }, [currentUser]);

    // ---- Carregar tarefas de origem externa baseadas no NOME do usuário ----
    useEffect(() => {
        if (MEU_PROJETO_DESATIVADO || !userName) return;

        const processExternalEtapas = (docs, sourceName, collectionName) => {
            return docs.map(d => {
                const data = d.data();
                const filteredTarefas = (data.tarefas || []).filter(t => t && t.responsavel === userName);
                return filteredTarefas.length > 0 ? {
                    id: `${collectionName}_${d.id}`,
                    text: `[${sourceName}] ${data.text || 'Etapa'}`,
                    tarefas: filteredTarefas,
                    isExternal: true,
                    isFlat: false,
                    sourceCol: collectionName,
                    originalId: d.id
                } : null;
            }).filter(Boolean);
        };

        const uIW = onSnapshot(query(collection(db, 'etapas')), snap => setExternalIW(processExternalEtapas(snap.docs, 'IW', 'etapas')));
        const uDir = onSnapshot(query(collection(db, 'etapas_direcao')), snap => setExternalDir(processExternalEtapas(snap.docs, 'Direção', 'etapas_direcao')));
        const uHC = onSnapshot(query(collection(db, 'etapas_homecare')), snap => setExternalHC(processExternalEtapas(snap.docs, 'Homecare', 'etapas_homecare')));

        const uQual = onSnapshot(query(collection(db, 'qualidade_tarefas')), snap => {
            const filteredQualidade = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
                const auds = t.auditores || (t.auditor ? [t.auditor] : []);
                const isResp = t.responsavel === userName || auds.includes(userName);
                return isResp;
            }).map(t => {
                // Formatar para manter consistência nas propriedades da tarefa
                return {
                    ...t,
                    text: t.titulo || t.text || 'Tarefa Qualidade',
                    dataInicio: t.data || t.dataInicio,
                    dataFim: t.data || t.dataFim
                };
            });

            if (filteredQualidade.length > 0) {
                setExternalQual([{
                    id: 'ext_qual',
                    text: '[Qualidade] Tarefas e Agendas',
                    tarefas: filteredQualidade,
                    isExternal: true,
                    isFlat: true,
                    sourceCol: 'qualidade_tarefas'
                }]);
            } else {
                setExternalQual([]);
            }
        });

        return () => { uIW(); uDir(); uHC(); uQual(); };
    }, [userName]);

    // ---- CRUD Etapas ----
    const handleAddEtapa = async (nome) => {
        const newOrder = etapas.length > 0 ? Math.max(...etapas.map(e => e.order || 0)) + 1 : 0;
        await addDoc(collection(db, ETAPAS_COLL), {
            text: nome,
            tarefas: [],
            order: newOrder,
            userId: currentUser.uid,
        });
    };

    const handleDeleteEtapa = async (id) => {
        if (window.confirm('Apagar esta etapa e todas as tarefas?'))
            await deleteDoc(doc(db, ETAPAS_COLL, id));
    };

    const handleUpdateEtapa = async (id, novoNome) => {
        await updateDoc(doc(db, ETAPAS_COLL, id), { text: novoNome });
    };

    function handleDragEnd(event) {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setEtapas(items => {
                const oldI = items.findIndex(i => i.id === active.id);
                const newI = items.findIndex(i => i.id === over.id);
                const newArr = arrayMove(items, oldI, newI);
                const batch = writeBatch(db);
                newArr.forEach((e, i) => batch.update(doc(db, ETAPAS_COLL, e.id), { order: i }));
                batch.commit();
                return newArr;
            });
        }
    }

    const toggleEtapa = (id) =>
        setExpandedEtapaIds(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

    // ---- CRUD Tarefas ----
    const handleAddTask = async (etapaId, newTask) => {
        const etapaRef = doc(db, ETAPAS_COLL, etapaId);
        const etapaDoc = await getDoc(etapaRef);
        if (etapaDoc.exists()) {
            // Força o responsável como o próprio usuário
            const taskWithOwner = { ...newTask, responsavel: userName, userId: currentUser.uid };
            await updateDoc(etapaRef, { tarefas: [...(etapaDoc.data().tarefas || []), taskWithOwner] });
        }
    };

    const handleDeleteTask = async (etapaId, tarefaId) => {
        const ref = doc(db, ETAPAS_COLL, etapaId);
        const d = await getDoc(ref);
        if (d.exists()) await updateDoc(ref, { tarefas: d.data().tarefas.filter(t => t && t.id !== tarefaId) });
    };

    const handleDuplicateTask = async (etapaId, tarefa) => {
        await handleAddTask(etapaId, { ...tarefa, id: Date.now(), status: 'Não iniciado', dataConclusao: null, comments: [] });
    };

    const handleMoveTask = async (currentEtapaId, targetEtapaId, task) => {
        if (!targetEtapaId || currentEtapaId === targetEtapaId) return;
        const srcRef = doc(db, ETAPAS_COLL, currentEtapaId);
        const srcDoc = await getDoc(srcRef);
        if (srcDoc.exists()) await updateDoc(srcRef, { tarefas: srcDoc.data().tarefas.filter(t => t.id !== task.id) });
        const tgtRef = doc(db, ETAPAS_COLL, targetEtapaId);
        const tgtDoc = await getDoc(tgtRef);
        if (tgtDoc.exists()) await updateDoc(tgtRef, { tarefas: [...(tgtDoc.data().tarefas || []), task] });
    };

    const handleUpdateTask = async (etapa, tarefa, campo, valor) => {
        const tarefaId = tarefa.id;
        
        if (etapa.isExternal) {
            if (etapa.isFlat) {
                const ref = doc(db, etapa.sourceCol, tarefaId);
                const updated = { [campo]: valor };
                if (campo === 'status') {
                    if (valor === 'Feito') updated.dataConclusao = new Date().toISOString().split('T')[0];
                    else updated.dataConclusao = null;
                }
                if (campo === 'text') updated.titulo = valor; // Adaptar prop titulo da Qualidade
                await updateDoc(ref, updated);
            } else {
                const ref = doc(db, etapa.sourceCol, etapa.originalId);
                const d = await getDoc(ref);
                if (d.exists()) {
                    const tarefas = d.data().tarefas.map(t => {
                        if (t && t.id === tarefaId) {
                            const updated = { ...t, [campo]: valor };
                            if (campo === 'status') {
                                if (valor === 'Feito' && !t.dataConclusao) updated.dataConclusao = new Date().toISOString().split('T')[0];
                                else if (valor !== 'Feito') updated.dataConclusao = null;
                            }
                            return updated;
                        }
                        return t;
                    });
                    await updateDoc(ref, { tarefas });
                }
            }
            return;
        }

        const ref = doc(db, ETAPAS_COLL, etapa.id);
        const d = await getDoc(ref);
        if (d.exists()) {
            const tarefas = d.data().tarefas.map(t => {
                if (t && t.id === tarefaId) {
                    const updated = { ...t, [campo]: valor };
                    if (campo === 'status') {
                        if (valor === 'Feito' && !t.dataConclusao)
                            updated.dataConclusao = new Date().toISOString().split('T')[0];
                        else if (valor !== 'Feito')
                            updated.dataConclusao = null;
                    }
                    return updated;
                }
                return t;
            });
            await updateDoc(ref, { tarefas });
        }
    };

    const handleAddComment = async (etapa, tarefa, text) => {
        const tarefaId = tarefa.id;
        const newComment = { text, userName, userEmail: currentUser.email, timestamp: Date.now() };

        if (etapa.isExternal) {
            if (etapa.isFlat) {
                const ref = doc(db, etapa.sourceCol, tarefaId);
                const d = await getDoc(ref);
                if (d.exists()) {
                    const comments = [...(d.data().comments || []), newComment];
                    await updateDoc(ref, { comments });
                    setSelectedTask(prev => ({ ...prev, comments }));
                }
            } else {
                const ref = doc(db, etapa.sourceCol, etapa.originalId);
                const d = await getDoc(ref);
                if (d.exists()) {
                    const tarefas = d.data().tarefas.map(t => {
                        if (t && t.id === tarefaId) {
                            const comments = [...(t.comments || []), newComment];
                            setSelectedTask(prev => ({ ...prev, comments }));
                            return { ...t, comments };
                        }
                        return t;
                    });
                    await updateDoc(ref, { tarefas });
                }
            }
            return;
        }

        const ref = doc(db, ETAPAS_COLL, etapa.id);
        const d = await getDoc(ref);
        if (d.exists()) {
            const newComment = { text, userName, userEmail: currentUser.email, timestamp: Date.now() };
            const tarefas = d.data().tarefas.map(t => {
                if (t && t.id === tarefaId) {
                    const comments = [...(t.comments || []), newComment];
                    setSelectedTask(prev => ({ ...prev, comments }));
                    return { ...t, comments };
                }
                return t;
            });
            await updateDoc(ref, { tarefas });
        }
    };

    const openCommentsModal = (etapa, task) => { setSelectedTask({ ...task, etapa_obj: etapa }); setIsCommentsModalOpen(true); };

    // ---- Filtragem ----
    const applyFiltersFunc = (etapasList) => {
        const hasFilter = filtroStatus.length > 0 || filtroPrazo !== 'todos' || filtroTexto;
        if (!hasFilter) return etapasList;
        return etapasList.map(etapa => ({
            ...etapa,
            tarefas: (etapa.tarefas || []).filter(t => {
                if (!t) return false;
                // Em qualidade_tarefas, 'Em andamento' pode estar abreviado dependendo do registro, a comparação exata cuida disso.
                const statusStr = t.status || 'Não iniciado';
                const statusMatch = filtroStatus.length === 0 || filtroStatus.includes(statusStr);
                const textoMatch = !filtroTexto || ((t.text || t.titulo || '').toLowerCase().includes(filtroTexto.toLowerCase()));
                const sp = calcularStatusPrazo(t.dataFim || t.data, t.status, t.dataConclusao);
                let prazoMatch = true;
                if (filtroPrazo === 'atrasado') prazoMatch = sp.classe === 'prazo-atrasado';
                else if (filtroPrazo === 'urgente') prazoMatch = sp.classe === 'prazo-urgente';
                else if (filtroPrazo === 'no_prazo') prazoMatch = sp.classe === 'prazo-ok' || sp.classe === 'prazo-concluido-ok';
                return statusMatch && prazoMatch && textoMatch;
            })
        }));
    };

    const filteredEtapas = useMemo(() => applyFiltersFunc(etapas), [etapas, filtroStatus, filtroPrazo, filtroTexto]);
    
    const filteredExternals = useMemo(() => {
        const extUnificada = [...externalIW, ...externalDir, ...externalHC, ...externalQual];
        return applyFiltersFunc(extUnificada).filter(e => e.tarefas && e.tarefas.length > 0);
    }, [externalIW, externalDir, externalHC, externalQual, filtroStatus, filtroPrazo, filtroTexto]);

    // Auto-expand ao filtrar
    useEffect(() => {
        if (filtroStatus.length > 0 || filtroPrazo !== 'todos' || filtroTexto) {
            const allE = [...filteredEtapas, ...filteredExternals];
            setExpandedEtapaIds(allE.filter(e => e.tarefas?.length > 0).map(e => e.id));
        }
    }, [filtroStatus, filtroPrazo, filtroTexto, filteredEtapas, filteredExternals]);

    if (isLoading) return <div style={{ padding: 40 }}>Carregando...</div>;
    if (!currentUser) return <div style={{ padding: 40 }}>Faça login para ver seu projeto.</div>;

    if (MEU_PROJETO_DESATIVADO) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Meu Projeto desativado</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
            </div>
        );
    }

    return (
        <div className="App">
            {/* Cabeçalho */}
            <div className="page-header">
                <div className="header-left">
                    <h2>Meu Projeto</h2>
                    <span style={{ color: '#888', fontSize: '0.85rem', marginLeft: 8 }}>👤 {userName}</span>
                </div>
                <div className="header-actions">
                    <button
                        className="reminder-btn"
                        style={{ backgroundColor: '#6f42c1', color: 'white', marginRight: 10 }}
                        onClick={openReminderManually}
                    >🔔 Próximos Vencimentos</button>
                    <button className="add-etapa-btn" onClick={() => setIsEtapaModalOpen(true)}>+ Adicionar Etapa</button>
                    <button className="add-task-btn" onClick={() => setIsTaskModalOpen(true)}>+ Adicionar Tarefa</button>
                </div>
            </div>

            {/* Filtros */}
            <div className="filter-bar-container">
                <div className="filter-group">
                    <label>Buscar tarefas:</label>
                    <input
                        type="text"
                        value={filtroTexto}
                        onChange={e => setFiltroTexto(e.target.value)}
                        placeholder="Digite para filtrar..."
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: 200 }}
                    />
                </div>
                <div className="filter-group">
                    <label>Status:</label>
                    <div style={{ display: 'flex', gap: 5 }}>
                        {['Não iniciado', 'Em andamento', 'Feito'].map(s => (
                            <button
                                key={s}
                                onClick={() => setFiltroStatus(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                                style={{
                                    padding: '6px 12px', borderRadius: 20, border: '1px solid #ccc',
                                    backgroundColor: filtroStatus.includes(s) ? '#6f42c1' : '#f8f9fa',
                                    color: filtroStatus.includes(s) ? '#fff' : '#333',
                                    cursor: 'pointer', fontSize: '0.85rem',
                                    fontWeight: filtroStatus.includes(s) ? 'bold' : 'normal',
                                    transition: 'all 0.2s'
                                }}
                            >{s}</button>
                        ))}
                    </div>
                </div>
                <div className="filter-group">
                    <label>Prazo:</label>
                    <select value={filtroPrazo} onChange={e => setFiltroPrazo(e.target.value)}>
                        <option value="todos">Todos</option>
                        <option value="no_prazo">No Prazo</option>
                        <option value="urgente">Termina Hoje</option>
                        <option value="atrasado">Atrasado</option>
                    </select>
                </div>
                <button className="clear-filters-btn" onClick={() => { setFiltroStatus([]); setFiltroPrazo('todos'); setFiltroTexto(''); }}>
                    Limpar Filtros
                </button>
            </div>

            {/* Modals */}
            <AddEtapaModal isOpen={isEtapaModalOpen} onClose={() => setIsEtapaModalOpen(false)} onAddEtapa={handleAddEtapa} />
            <AddTaskModal
                isOpen={isTaskModalOpen}
                onClose={() => setIsTaskModalOpen(false)}
                onAddTask={handleAddTask}
                etapas={etapas}
                responsaveis={[{ id: 'self', nome: userName }]}
            />
            <CommentsModal
                isOpen={isCommentsModalOpen}
                onClose={() => setIsCommentsModalOpen(false)}
                task={selectedTask}
                onAddComment={(taskId, text) => {
                    const obj = selectedTask?.etapa_obj;
                    return handleAddComment(obj, selectedTask, text);
                }}
            />

            {/* Estado vazio */}
            {etapas.length === 0 && (
                <div style={{ padding: '60px 40px', textAlign: 'center', color: '#888' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 16 }}>📋</div>
                    <h3 style={{ margin: '0 0 8px', color: '#555' }}>Seu projeto pessoal está vazio</h3>
                    <p style={{ margin: 0, fontSize: '0.95rem' }}>Clique em <strong>"+ Adicionar Etapa"</strong> para começar a organizar suas tarefas.</p>
                </div>
            )}

            {/* Board de etapas */}
            <main className="task-container">
            	
                {/* --- RENDERIZAÇÃO DE TAREFAS EXTERNAS (Apenas Leitura/Parcial) --- */}
                {filteredExternals.length > 0 && (
                    <div style={{ padding: '15px 15px 0 15px', marginBottom: '25px', backgroundColor: '#fdfbfe', borderRadius: '8px', border: '1px solid #e1d5eb' }}>
                        <h4 style={{ color: '#6f42c1', marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '1.2rem' }}>📥</span> Demandas Externas (Outros Projetos / Qualidade)
                        </h4>
                        {filteredExternals.map(etapa => (
                            <PseudoEtapaExterna
                                key={etapa.id}
                                etapa={etapa}
                                expandedEtapaIds={expandedEtapaIds}
                                toggleEtapa={toggleEtapa}
                            >
                                {(etapa.tarefas && etapa.tarefas.length > 0) && (
                                    <div className="tarefa-header tarefa-header-direcao" style={{ gridTemplateColumns: '1fr 140px 100px 100px 100px 100px 100px 60px 100px' }}>
                                        <div className="header-tarefa-text">Tarefa</div>
                                        <div className="header-tarefa-responsavel">Responsável</div>
                                        <div className="header-tarefa-prioridade">Prioridade</div>
                                        <div className="header-tarefa-data">Início</div>
                                        <div className="header-tarefa-data">Fim</div>
                                        <div className="header-tarefa-data">Conclusão</div>
                                        <div className="header-tarefa-status">Status</div>
                                        <div className="header-tarefa-comment">Coment.</div>
                                        <div className="header-tarefa-calculado">Prazo</div>
                                    </div>
                                )}
                                {(etapa.tarefas || [])
                                    .sort((a, b) => {
                                        if (!a?.dataInicio && !b?.dataInicio) return 0;
                                        if (!a?.dataInicio) return 1;
                                        if (!b?.dataInicio) return -1;
                                        return new Date(a.dataInicio) - new Date(b.dataInicio);
                                    })
                                    .map((tarefa, idx) => {
                                        if (!tarefa) return null;
                                        const sp = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
                                        const commentCount = tarefa.comments?.length || 0;
                                        
                                        // Variáveis específicas de permissão externa
                                        const isReadOnly = true; 

                                        return (
                                            <div key={tarefa.id || idx} className="tarefa-card-detalhada tarefa-card-detalhada-direcao" style={{ gridTemplateColumns: '1fr 140px 100px 100px 100px 100px 100px 60px 100px' }}>
                                                <div className="tarefa-text">
                                                    <input type="text" value={tarefa.text || ''} className="text-input task-description-input" disabled={isReadOnly} />
                                                </div>
                                                <div className="tarefa-responsavel" data-label="Responsável">
                                                    <span style={{ fontSize: '0.82rem', color: '#555', padding: '4px 8px', backgroundColor: '#f0f4ff', borderRadius: 6, display: 'inline-block' }}>
                                                        👤 {tarefa.responsavel || tarefa.auditor || userName}
                                                    </span>
                                                </div>
                                                <div className="tarefa-prioridade" data-label="Prioridade">
                                                    <span className={`prioridade-tag prioridade-${(tarefa.prioridade || 'média').toLowerCase()}`} style={{ opacity: 0.8 }}>
                                                        {tarefa.prioridade || 'Média'}
                                                    </span>
                                                </div>
                                                <div className="tarefa-data" data-label="Início">
                                                    <input type="date" value={tarefa.dataInicio || ''} className="date-input" disabled={isReadOnly} />
                                                </div>
                                                <div className="tarefa-data" data-label="Fim">
                                                    <input type="date" value={tarefa.dataFim || ''} className="date-input" disabled={isReadOnly} />
                                                </div>
                                                <div className="tarefa-data" data-label="Conclusão">
                                                    {tarefa.status === 'Feito'
                                                        ? <input type="date" value={tarefa.dataConclusao || ''} className="date-input"
                                                            onChange={e => handleUpdateTask(etapa, tarefa, 'dataConclusao', e.target.value)} />
                                                        : <span>--</span>
                                                    }
                                                </div>
                                                <div className="tarefa-status" data-label="Status">
                                                    <select
                                                        value={tarefa.status || 'Não iniciado'}
                                                        className={`status-select status-${(tarefa.status || 'nao-iniciado').replace(/\s+/g, '-').toLowerCase()}`}
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'status', e.target.value)}
                                                    >
                                                        <option value="Não iniciado">Não iniciado</option>
                                                        <option value="Pendente">Pendente</option>
                                                        <option value="Em andamento">Em andamento</option>
                                                        <option value="Feito">Feito</option>
                                                    </select>
                                                </div>
                                                <div className="tarefa-comment">
                                                    <button className="comment-btn" onClick={() => openCommentsModal(etapa, tarefa)}>
                                                        💬 <span className="comment-count">{commentCount}</span>
                                                    </button>
                                                </div>
                                                <div className="tarefa-calculado" data-label="Prazo">
                                                    <span className={sp.classe}>{sp.texto}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </PseudoEtapaExterna>
                        ))}
                    </div>
                )}


                {/* --- RENDERIZAÇÃO DE TAREFAS PESSOAIS --- */}
                <h4 style={{ color: '#555', marginLeft: '15px' }}>📌 Tarefas do Projeto Pessoal</h4>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={etapas.map(e => e.id)} strategy={verticalListSortingStrategy}>
                        {filteredEtapas.map(etapa => (
                            <SortableEtapa
                                key={etapa.id}
                                etapa={etapa}
                                expandedEtapaIds={expandedEtapaIds}
                                toggleEtapa={toggleEtapa}
                                handleUpdateEtapa={handleUpdateEtapa}
                                handleDeleteEtapa={handleDeleteEtapa}
                            >
                                {etapa.tarefas && etapa.tarefas.length > 0 && (
                                    <div className="tarefa-header tarefa-header-direcao">
                                        <div className="header-tarefa-text">Tarefa</div>
                                        <div className="header-tarefa-responsavel">Responsável</div>
                                        <div className="header-tarefa-prioridade">Prioridade</div>
                                        <div className="header-tarefa-data">Início</div>
                                        <div className="header-tarefa-data">Fim</div>
                                        <div className="header-tarefa-data">Conclusão</div>
                                        <div className="header-tarefa-status">Status</div>
                                        <div className="header-tarefa-comment">Coment.</div>
                                        <div className="header-tarefa-calculado">Prazo</div>
                                        <div className="header-tarefa-move">Mover</div>
                                        <div className="header-tarefa-action">Ações</div>
                                    </div>
                                )}
                                {(etapa.tarefas || [])
                                    .sort((a, b) => {
                                        if (!a?.dataInicio && !b?.dataInicio) return 0;
                                        if (!a?.dataInicio) return 1;
                                        if (!b?.dataInicio) return -1;
                                        return new Date(a.dataInicio) - new Date(b.dataInicio);
                                    })
                                    .map((tarefa, idx) => {
                                        if (!tarefa) return null;
                                        const sp = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
                                        const commentCount = tarefa.comments?.length || 0;
                                        return (
                                            <div key={tarefa.id || idx} className="tarefa-card-detalhada tarefa-card-detalhada-direcao">
                                                <div className="tarefa-text">
                                                    <input type="text" value={tarefa.text || ''} className="text-input task-description-input"
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'text', e.target.value)} />
                                                </div>
                                                <div className="tarefa-responsavel" data-label="Responsável">
                                                    <span style={{ fontSize: '0.82rem', color: '#555', padding: '4px 8px', backgroundColor: '#f0f4ff', borderRadius: 6, display: 'inline-block' }}>
                                                        👤 {tarefa.responsavel || userName}
                                                    </span>
                                                </div>
                                                <div className="tarefa-prioridade" data-label="Prioridade">
                                                    <select
                                                        value={tarefa.prioridade || 'Média'}
                                                        className={`prioridade-tag prioridade-${(tarefa.prioridade || 'média').toLowerCase()}`}
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'prioridade', e.target.value)}
                                                    >
                                                        <option value="Baixa">Baixa</option>
                                                        <option value="Média">Média</option>
                                                        <option value="Alta">Alta</option>
                                                        <option value="Crítico">Crítico</option>
                                                    </select>
                                                </div>
                                                <div className="tarefa-data" data-label="Início">
                                                    <input type="date" value={tarefa.dataInicio || ''} className="date-input"
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'dataInicio', e.target.value)} />
                                                </div>
                                                <div className="tarefa-data" data-label="Fim">
                                                    <input type="date" value={tarefa.dataFim || ''} className="date-input"
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'dataFim', e.target.value)} />
                                                </div>
                                                <div className="tarefa-data" data-label="Conclusão">
                                                    {tarefa.status === 'Feito'
                                                        ? <input type="date" value={tarefa.dataConclusao || ''} className="date-input"
                                                            onChange={e => handleUpdateTask(etapa, tarefa, 'dataConclusao', e.target.value)} />
                                                        : <span>--</span>
                                                    }
                                                </div>
                                                <div className="tarefa-status" data-label="Status">
                                                    <select
                                                        value={tarefa.status || 'Não iniciado'}
                                                        className={`status-select status-${(tarefa.status || 'nao-iniciado').replace(/\s+/g, '-').toLowerCase()}`}
                                                        onChange={e => handleUpdateTask(etapa, tarefa, 'status', e.target.value)}
                                                    >
                                                        <option value="Não iniciado">Não iniciado</option>
                                                        <option value="Em andamento">Em andamento</option>
                                                        <option value="Feito">Feito</option>
                                                    </select>
                                                </div>
                                                <div className="tarefa-comment">
                                                    <button className="comment-btn" onClick={() => openCommentsModal(etapa, tarefa)}>
                                                        💬 <span className="comment-count">{commentCount}</span>
                                                    </button>
                                                </div>
                                                <div className="tarefa-calculado" data-label="Prazo">
                                                    <span className={sp.classe}>{sp.texto}</span>
                                                </div>
                                                <div className="tarefa-move" data-label="Mover">
                                                    <select className="move-select" value={etapa.id}
                                                        onChange={e => handleMoveTask(etapa.id, e.target.value, tarefa)}
                                                        style={{ width: '100%', fontSize: '0.8rem', padding: 2 }}
                                                    >
                                                        <option value={etapa.id}>Mover para...</option>
                                                        {etapas.filter(e => e.id !== etapa.id).map(e => (
                                                            <option key={e.id} value={e.id}>{e.text}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="tarefa-action" data-label="Ações">
                                                    <button onClick={() => handleDuplicateTask(etapa.id, tarefa)}
                                                        className="duplicate-btn" title="Duplicar"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', marginRight: 5 }}>🔄</button>
                                                    <button onClick={() => handleDeleteTask(etapa.id, tarefa.id)}
                                                        className="delete-btn" style={{ margin: 0 }}>🗑️</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </SortableEtapa>
                        ))}
                    </SortableContext>
                </DndContext>
            </main>

            <DeadlineReminder
                isTodayOpen={isTodayOpen}
                isWeekOpen={isWeekOpen}
                todayItems={todayItems}
                weekItems={weekItems}
                onCloseTodayPopup={closeTodayPopup}
                onCloseWeekPopup={closeWeekPopup}
                onDismissToday={dismissToday}
                type="task"
            />
        </div>
    );
}

export default MeuProjeto;
